/**
 * @module app.shared
 */

import { join, observable, signalBindings, Container, computedFrom, Aurelia } from 'aurelia-framework';
import { IEssetteConfig, defaultConfig } from 'app/shared/config/config-base';
import { AppModule, IDynamicView, DynamicViews } from 'app/shared/config/modules';
import { Indexed } from 'app/shared/lib/types';
import { sleep, mergeObjects } from 'app/shared/lib/utilities';
import { DateValue, DateTimeValue } from 'app/shared/lib/date-time';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ISessionTenant, ISessionUser, ISessionLocation, ISessionUpdate, ISessionUserContext, ISessionApplication } from 'app/shared/lib/session/session-types';
import { StorageService, ClientStorage, StorageArea } from 'app/shared/lib/client-storage';
import { emptyUser, emptyTenant, emptyLocation, emptyUserContext, emptyApplication } from 'app/shared/lib/session/session-defaults';
import { signalNames } from 'app/shared/lib/signals';
import { IComponentConfig } from 'app/shared/elements/ess-dynamic-grid';
import { httpRequest } from 'app/shared/services/http-service';

// work around construction order issue
function createLogger(env: EnvironmentService): AppLogger {
    const logger = new AppLogger();
    logger.source = env;
    logger.target = 'console';
    return logger;
}

export class EnvironmentService {

    // NOTE: due to the order in which things are loaded @persist values cannot be declared on this class

    private logger = createLogger(this);

    public config: IEssetteConfig = defaultConfig;

    public baseUrl: string = '/';

    @observable
    private currentDate: DateValue = DateValue.today('browser');

    @computedFrom('currentDate')
    public get today(): string {
        return this.currentDate.value || '';
    }

    @computedFrom('currentDate')
    public get yesterday(): string {
        return this.currentDate.add(-1, 'days').value || '';
    }

    @computedFrom('currentDate')
    public get tomorrow(): string {
        return this.currentDate.add(1, 'days').value || '';
    }

    public storage!: ClientStorage;

    @observable
    public application: ISessionApplication;

    @observable
    public tenant: ISessionTenant;

    @observable
    public user: ISessionUser;

    @observable
    public context: ISessionUserContext;

    @observable
    public location: ISessionLocation;

    @observable
    public language: string = defaultConfig.i18n.language;

    @observable
    public currency: string = defaultConfig.i18n.currency;

    @observable
    public timeZone: string = 'browser';

    @observable
    public displayTimeZone: string = 'browser';

    @observable
    public locked: boolean = false;

    private suspendRefreshLocale = false;
    private loaded: boolean = false;
    private started: boolean = false;
    private configPath: string = '~/environment-config.json';

    private container: Container | null = null;

    private dynamicViewCache = new Map<string, DynamicViews>();

    constructor() {
        this.suspendRefreshLocale = true;
        try {
            this.application = emptyApplication;
            this.tenant = emptyTenant;
            this.user = emptyUser;
            this.context = emptyUserContext;
            this.location = emptyLocation;
        }
        finally {
            this.suspendRefreshLocale = false;
        }
    }

    public async load(container: Container): Promise<void> {
        if(this.loaded) { return; }

        this.container = container;
        container.registerInstance(EnvironmentService, this);

        this.baseUrl = window.location.pathname;

        const configUrl = this.resolveUrl(this.configPath);
        const loadedConfig = await httpRequest(configUrl, { format: 'json', cache: 'none' });

        // apply defaults (deep merge so nested properties from defaultConfig are preserved)
        this.config = mergeObjects({ ...defaultConfig }, loadedConfig);
        console.log('ENVIRONMENT CONFIG LOADED', { environmentConfig: this.config });

        this.storage = new ClientStorage(this.config.instanceScope);
        container.registerInstance(ClientStorage);
        container.registerInstance(StorageService, new StorageService(this.storage));

        this.registerDebugObject('env', this);
        this.registerDebugObject('DateValue', DateValue);
        this.registerDebugObject('DateTimeValue', DateTimeValue);
        this.registerDebugObject('storage', this.storage);
        this.registerDebugObject('stored', () => this.queryStorage());
        this.registerDebugObject('date', (v: any) => {
            if(typeof v === 'string') {
                if(v.length === 10) {
                    return new DateValue(v);
                }
                else if(v.length > 10) {
                    return new DateTimeValue(v);
                }
            }
            return null;
        });

        this.loaded = true;
    }

    public start(): void {
        if(this.started) { return; }

        this.generateClockTickMinuteAsync();
        this.started = true;
    }

    public isAppVisible(): boolean {
        return !window.document.hidden && !this.locked;
    }

    /**
     * Register an object global value (on the `window` object) under `essette.key`.
     * @param key key to register under
     * @param value object, function, or value to register
     */
    public registerDebugObject(key: string, value: any): void {
        if(this.config.debug) {
            const target = <any>window;
            if(!target.essette) { target.essette = {} };
            target.essette[key] = value;
        }
    }

    /**
     * Now is implemented as a function so that if you bind to it, the value will not update
     * and will stay bound at this current point in time.
     */
    public now(): string {
        const now = DateTimeValue.now().value || '';
        return now;
    }

    public updateSession(update: Partial<ISessionUpdate>): void {
        this.suspendRefreshLocale = true;
        try {
            this.logger.info(`Updating: ${Object.keys(update).join(', ')}`);
            if(update.locked !== undefined) {
                this.locked = update.locked || false;
            }
            if(update.application !== undefined) {
                this.application = update.application || emptyApplication;
            }
            if(update.tenant !== undefined) {
                this.tenant = update.tenant || emptyTenant;
            }
            if(update.user !== undefined) {
                this.user = update.user || emptyUser;
            }
            if(update.context !== undefined) {
                this.context = update.context || emptyUserContext;
            }
            if(update.location !== undefined) {
                this.location = update.location || emptyLocation;
            }
        }
        finally {
            this.suspendRefreshLocale = false;
        }
        this.refreshLocale();
    }

    /**
     * Resolves a relative URL, supporting '~/' as an application root indicator
     *
     * example baseUrl:  "/template/v4.0/suite/"
     * example input:  ~/cm/case/#/900
     * example output: /template/v4.0/suite/cm/case/#/900
     *
     * @param url the resolved URL
     */
    public resolveUrl(url: string | null): string {
        const rootRelative = '~/';
        const schemeRelative = '//';

        const urlString = String(url);
        if(url) {
            if(urlString.startsWith(rootRelative)) {
                // relative to application baseUrl
                const relativeUrl = urlString.substr(rootRelative.length);
                return join(this.baseUrl, relativeUrl);
            }
            else if(urlString.startsWith(schemeRelative)) {
                // relative to current window scheme (protocol)
                return `${window.location.protocol}${urlString}`;
            }
        }
        // return all other relative/absolute urls as-is
        return urlString;
    }

    public getModules(): ReadonlyArray<AppModule> {
        if(this.container) {
            return [...this.container.getAll(AppModule.registrationKey)];
        }
        return [];
    }

    /**
     * Get all dynamic views defined in all modules for a given scope
     * @param scope the scope code (e.g. 'CD.MemberSummary')
     */
    public getDynamicViews(scope: string): DynamicViews {
        const cached = this.dynamicViewCache.get(scope);
        if(cached) { return cached; }

        // merge all modules' getDynamicView(scope) results
        const results: Indexed<IDynamicView[]> = {};
        for(const module of this.getModules()) {
            const lookup = module.getDynamicViews(scope);
            for(const key in lookup) {
                const views = lookup[key];
                if(!views) { continue; }
                let slot = results[key];
                if(!slot) {
                    slot = results[key] = [];
                }
                slot.push(...views);
            }
        }
        return results;
    }

    /**
     * Get a single dynamic view for a given scope and key
     * @param scope the scope code (e.g. 'CD.MemberSummary')
     * @param key the view key
     */
    public getDynamicViewByKey(scope: string, key: string): IDynamicView | null {
        const views = this.getDynamicViews(scope);
        const results = views[key];
        if(results && results.length > 0) {
            return results[0];
        }
        return null;
    }

    /**
     * Get all dynamic views for a given scope and key
     * @param scope the scope code (e.g. 'CD.MemberSummary')
     * @param key the view key
     */
    public getDynamicViewsByKey(scope: string, key: string): ReadonlyArray<IDynamicView> {
        const views = this.getDynamicViews(scope);
        return views[key] || [];
    }

    /**
     * Gets a list of custom components specified in the environment config.
     * @param key The key to match components against.
     * @returns {IComponentConfig[]} The components available for that key.
     */
    public getCustomComponents(key: string): IComponentConfig[] {
        return Object.entries(this.config.components || {})
            .filter(([_path, component]) => {
                const { includes, excludes } = component;
                return (includes?.length && includes?.includes(key))
                    || (excludes?.length && !excludes?.includes(key))
                    || (!includes && !excludes);
            })
            .map(([path, component]) => {
                const isViewModelPair = !/\.html?$/.test(path);
                const compose = document.createElement('compose');
                compose.setAttribute(isViewModelPair ? 'view-model' : 'view', `app/${path}`);
                const match = /([^\.\/]*)(?:\.\w+)?$/.exec(path);
                const name = match ? match[1] : path;

                // NOTE [Matt] Order matters here. If the component specifies a name, it will override the default name pulled from the path. However,
                // if the component specifies a key or content, it will be ignored. Just to prevent any mistakes? Any other properties specified will
                // be pulled from the component, and we'd specifically be looking for height and width at the time of writing this comment.
                return {
                    name,
                    height: 1,
                    width: 1,
                    ...component,
                    key: path,
                    content: compose
                } as IComponentConfig;
            });
    }

    /**
     * Get all dynamic views for a given scope in the order they were defined
     * @param scope the scope code (e.g. 'CD.MemberSummary')
     */
    public getAllDynamicViews(scope: string): ReadonlyArray<IDynamicView> {
        const views = this.getDynamicViews(scope);
        const results: IDynamicView[] = [];
        for(const key in views) {
            const list = views[key];
            if(list) {
                results.push(...list);
            }
        }
        return results;
    }

    public isModuleEnabled(module: string): boolean {
        return this.config.features.indexOf(module) >= 0 || this.config.modules.indexOf(module) >= 0;
    }

    protected userChanged(newUser?: ISessionUser, oldUser?: ISessionUser): void {
        if(newUser !== oldUser) {
            if(newUser && newUser.isLoggedIn) {
                this.logger.info(`User Changed: '${newUser.description}'.`);
            }
            else {
                this.logger.info('User Changed: (none).');
            }
            this.refreshLocale();
        }
    }

    protected tenantChanged(newTenant?: ISessionTenant, oldTenant?: ISessionTenant): void {
        if(newTenant !== oldTenant) {
            if(newTenant && newTenant.isSelected) {
                this.logger.info(`Tenant Changed: '${newTenant.code}'.`);
            }
            else {
                this.logger.info('Tenant Changed: (none).');
            }
            this.refreshLocale();
        }
    }

    protected refreshLocale() {
        if(this.suspendRefreshLocale) { return; }
        this.timeZone =
            (this.tenant && this.tenant.timeZone) ||
            'browser';
        this.displayTimeZone =
            (this.user && this.user.timeZone) ||
            (this.tenant && this.tenant.timeZone) ||
            'browser';
        this.language =
            (this.user && this.user.language) ||
            (this.tenant && this.tenant.language) ||
            this.config.i18n.language;
    }

    protected languageChanged(newLanguage: string, oldLanguage: string): void {
        if(newLanguage !== oldLanguage) {
            this.logger.info(`Language Changed: '${newLanguage}'.`);
            // TODO: trigger i18n
        }
    }

    protected timeZoneChanged(newTimeZone: string | null, oldTimeZone: string | null): void {
        if(newTimeZone !== oldTimeZone) {
            this.logger.info(`Time Zone Changed: '${newTimeZone}'.`);
            this.updateCurrentDate();
            // signal time zone changed
            // signal clock tick minute because "current date" may have changed
            signalBindings(signalNames.timeZoneChanged);
            signalBindings(signalNames.clockTickMinute);
            signalBindings(signalNames.clockTickSecond);
        }
    }

    protected displayTimeZoneChanged(newTimeZone: string | null, oldTimeZone: string | null): void {
        if(newTimeZone !== oldTimeZone) {
            this.logger.info(`Display Time Zone Changed: '${newTimeZone}'.`);
            // signal time zone changed
            signalBindings(signalNames.timeZoneChanged);
        }
    }

    private updateCurrentDate() {
        // update current date
        const newDate = DateValue.today(this.timeZone);
        if(!newDate.isEqual(this.currentDate)) {
            this.logger.info(`Current Date Changed: '${newDate}'.`);
            this.currentDate = newDate;
        }
    }

    private async generateClockTickMinuteAsync(): Promise<void> {
        let utcStart = new Date();
        let secondsUntilNextMinute = 60 - utcStart.getSeconds();

        while(true) {
            if(secondsUntilNextMinute > 0) {
                await sleep(secondsUntilNextMinute * 1000); // convert to ms
            }
            // every second we call updateCurrentDate, which will check to see if the new date differs from the old one
            this.updateCurrentDate();
            secondsUntilNextMinute = 60;
            signalBindings(signalNames.clockTickMinute);
        }
    }

    private queryStorage(): any {
        const result: any = { instanceScope: this.storage.instanceScope };
        const areas: StorageArea[] = ['local', 'session'];
        for(const area of areas) {
            const keys = this.storage.getKeys(area);
            const areaKey = `$${area}Storage`;
            const areaResult = result[areaKey] = <any>{};
            for(const key of keys) {
                areaResult[key] = this.storage.getObject(key);
            }
        }
        return result;
    }

}
